import { Component, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormGroup,
  UntypedFormBuilder,
  ValidatorFn,
} from '@angular/forms';
import { AranDrawerModalService } from '@aran-client/aran-web-ui-toolkit';
import { Store } from '@ngxs/store';
import { debounceTime, Observable, tap } from 'rxjs';
import { PagingConfig } from 'src/app/core/models/paging-config.model';
import { FilterService } from 'src/app/services/filter/filter.service';
import { StoreHelperService } from 'src/app/shared/services/store-helper/store-helper.service';
import { CreateServiceRequestType } from 'src/app/shared/types/requests.types';
import * as requestTypes from 'src/app/shared/types/requests.types';
import { RequestsAction } from 'src/app/store/requests/requests.action';
import { RequestsState } from 'src/app/store/requests/requests.state';

@Component({
  selector: 'app-create',
  templateUrl: './create.component.html',
  styleUrls: ['./create.component.scss'],
})
export class CreateComponent implements OnInit {
  currentPage: number = 1;
  itemsPerPage: number = 10;
  maxSize: number = 5;
  totalItems: number = 0;
  pageSizeOptions: number[] = [5, 10, 25, 100];

  pagingConfig: { [key: string]: PagingConfig } = {};
  getAirportKey = (index: number) => `AirportHeliport-${index}`;
  getFirKey = (index: number) => `Airspace-${index}`;
  getScenarioKey = (index: number) => `Scenario-${index}`;

  filters: { [key: string]: string } = {
    Scenario: '',
    AirportHeliport: '',
    Airspace: '',
  };

  selectedIndexes: number[] = [];

  originalCreateInfo$: Observable<requestTypes.CreateInfoResponse | null> =
    this.store.select(RequestsState.getCreateInfo());

  createInfo$: Observable<requestTypes.CreateInfoResponse | null> =
    this.originalCreateInfo$;

  allServices: requestTypes.CreateInfoResponse | null = null;

  isLoading$: Observable<boolean> = this.store.select(
    RequestsState.getIsLoading()
  );

  form!: FormGroup<any>;

  airportsInfoText: string =
    'The selected Airport and FIR correspond to the Location indicated in NOTAM Item A.';

  constructor(
    private formBuilder: UntypedFormBuilder,
    private aranDrawerModalService: AranDrawerModalService,
    private store: Store,
    private filterService: FilterService,
    private storeHelperService: StoreHelperService
  ) {}

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      topics: this.formBuilder.array(
        [],
        this.atLeastOneSubTopicSelectedValidator()
      ),
    });

    this.createInfo$.subscribe((data) => {
      if (data) {
        this.allServices = data;
      }
    });

    let firstChangeSkipped = false;
    this.topicsArray.valueChanges.pipe(debounceTime(100)).subscribe(() => {
      if (!firstChangeSkipped) {
        firstChangeSkipped = true;
        return;
      }

      this.topicsArray.markAllAsTouched();
    });

    this.getCreateInfoList();
  }

  private atLeastOneSubTopicSelectedValidator(): ValidatorFn {
    return (formArray: AbstractControl): { [key: string]: any } | null => {
      if (!(formArray instanceof FormArray)) return null;

      const controls = formArray.controls;
      let hasInvalidSubTopic2 = false;
      let hasInvalidTopicAreaSelection = false;
      let hasMissingSubTopicForTopicArea = false;

      const hasNonRestrictedOption = controls.some((group) => {
        const selectedOption = group.get('subTopicSelectedOption')?.value;
        return (
          selectedOption !== null &&
          selectedOption !== 0 &&
          selectedOption !== 2
        );
      });

      for (let i = 0; i < controls.length; i++) {
        const group = controls[i];

        const selectedSubOption = group.get('subTopicSelectedOption')?.value;
        const subTopicIds = group.get('subTopicIds')?.value;

        const topicAreaSelectedOption = group.get(
          'topicAreaSelectedOption'
        )?.value;
        const topicAreaIds = group.get('topicAreaIds')?.value;

        const topicService = this.allServices?.topics?.[i];
        const topicAreas = topicService?.topicAreas ?? [];

        const hasTopicAreaInput =
          (topicAreaSelectedOption !== null && topicAreaSelectedOption !== 0) ||
          (() => {
            if (!Array.isArray(topicAreaIds) || topicAreaIds.length === 0)
              return false;

            const selectedAreaObjects = topicAreas.filter((area) =>
              topicAreaIds.includes(area.id)
            );

            const onlyAirportOrHeliport = selectedAreaObjects.every(
              (a) => a.type === 'Heliport' || a.type === 'Aerodrome'
            );

            return !onlyAirportOrHeliport;
          })();

        if (
          hasTopicAreaInput &&
          (selectedSubOption === null ||
            selectedSubOption === 0 ||
            selectedSubOption === undefined)
        ) {
          hasMissingSubTopicForTopicArea = true;
        }

        if (selectedSubOption === 2) {
          if (!Array.isArray(subTopicIds) || subTopicIds.length === 0) {
            hasInvalidSubTopic2 = true;
          }
        }

        if (
          topicAreas.length > 0 &&
          selectedSubOption !== null &&
          selectedSubOption !== 0
        ) {
          const selectedAreaObjects = topicAreas.filter(
            (area) =>
              Array.isArray(topicAreaIds) && topicAreaIds.includes(area.id)
          );

          const hasAirspace = selectedAreaObjects.some(
            (a) => a.type === 'Airspace'
          );
          const hasAirportOrHeliport = selectedAreaObjects.some(
            (a) => a.type === 'Heliport' || a.type === 'Aerodrome'
          );

          if (topicAreaSelectedOption === 4 && !hasAirportOrHeliport) {
            hasInvalidTopicAreaSelection = true;
          }

          if (
            (topicAreaSelectedOption === null ||
              topicAreaSelectedOption === 0) &&
            hasAirspace &&
            hasAirportOrHeliport
          ) {
          } else if (
            (topicAreaSelectedOption === null ||
              topicAreaSelectedOption === 0) &&
            hasAirspace
          ) {
          } else {
            if (
              topicAreaSelectedOption === null ||
              topicAreaSelectedOption === undefined ||
              topicAreaSelectedOption === 0
            ) {
              hasInvalidTopicAreaSelection = true;
            }
          }
        }
      }

      if (hasInvalidSubTopic2) {
        return { subTopic2MustHaveIds: true };
      }

      if (hasInvalidTopicAreaSelection) {
        return { topicAreaSelectionInvalid: true };
      }

      if (hasMissingSubTopicForTopicArea) {
        return { subTopicRequiredForTopicArea: true };
      }

      if (hasNonRestrictedOption) {
        return null;
      }

      const hasAtLeastOneSelected = controls.some((group) => {
        const subTopicIds = group.get('subTopicIds')?.value;
        const subSelectOptionAtleast = group.get(
          'subTopicSelectedOption'
        )?.value;
        return (
          Array.isArray(subTopicIds) &&
          subTopicIds.length > 0 &&
          subSelectOptionAtleast !== 0
        );
      });

      return hasAtLeastOneSelected
        ? null
        : { atLeastOneSubTopicRequired: true };
    };
  }

  get topicsArray(): FormArray {
    return this.form.get('topics') as FormArray;
  }

  shouldShowTopicsError(errorKey: string): boolean {
    const topics = this.topicsArray;
    return (
      !!topics?.hasError(errorKey) &&
      (topics.touched || topics.dirty || this.topicsControlsTouched())
    );
  }

  private topicsControlsTouched(): boolean {
    return this.topicsArray.controls.some((ctrl) => ctrl.touched || ctrl.dirty);
  }

  hasTopicErrors(): boolean {
    const errors = [
      'subTopic2MustHaveIds',
      'topicAreaSelectionInvalid',
      'subTopicRequiredForTopicArea',
      'atLeastOneSubTopicRequired',
    ];

    return errors.some((errorKey) => this.shouldShowTopicsError(errorKey));
  }

  getSubTopicIds(index: number): number[] {
    const topicsArray = this.form.get('topics') as unknown as FormArray;
    const topicGroup = topicsArray.at(index) as FormGroup;
    return topicGroup.get('subTopicIds')?.value || [];
  }

  isAreaChecked(index: number): number[] {
    const topicsArray = this.form.get('topics') as unknown as FormArray;
    const topicGroup = topicsArray.at(index) as FormGroup;

    return topicGroup.get('topicAreaIds')?.value || [];
  }

  onSubTopicCheckboxChange(
    event: any,
    topicIndex: number,
    subTopicId: number | string | null
  ): void {
    const topicsFormArray = this.form.get('topics') as unknown as FormArray;
    const subTopicArray = topicsFormArray
      .at(topicIndex)
      .get('subTopicIds') as FormArray;

    if (event.checked) {
      if (!subTopicArray.controls.some((c) => c.value === subTopicId)) {
        subTopicArray.push(this.formBuilder.control(subTopicId));
      }
    } else {
      const index = subTopicArray.controls.findIndex(
        (c) => c.value === subTopicId
      );
      if (index !== -1) subTopicArray.removeAt(index);
    }
  }

  onAreaCheckboxChange(
    event: any,
    topicIndex: number,
    areaId: string | number
  ): void {
    const topicsFormArray = this.form.get('topics') as unknown as FormArray;
    const areaArray = topicsFormArray
      .at(topicIndex)
      .get('topicAreaIds') as FormArray;

    if (event.checked) {
      if (!areaArray.controls.some((c) => c.value === areaId)) {
        areaArray.push(this.formBuilder.control(areaId));
      }
    } else {
      const index = areaArray.controls.findIndex((c) => c.value === areaId);
      if (index !== -1) {
        areaArray.removeAt(index);
      }
    }
  }

  areAllSubTopicsSelected(topicIndex: number, subTopics: any[]): boolean {
    const selected = this.getSubTopicIds(topicIndex);
    const allIds = subTopics.map((s) => s.id);
    return allIds.every((id) => selected.includes(id));
  }

  isSomeSubTopicsSelected(topicIndex: number, subTopics: any[]): boolean {
    const selected = this.getSubTopicIds(topicIndex);
    const allIds = subTopics.map((s) => s.id);
    return selected.length > 0 && selected.length < allIds.length;
  }
  onSelectAllSubTopics(event: any, topicIndex: number, subTopics: any[]): void {
    const topicsFormArray = this.form.get('topics') as unknown as FormArray;
    const subTopicArray = topicsFormArray
      .at(topicIndex)
      .get('subTopicIds') as FormArray;

    if (event.checked) {
      subTopics.forEach((subTopic) => {
        const exists = subTopicArray.controls.some(
          (c) => c.value === subTopic.id
        );
        if (!exists) {
          subTopicArray.push(this.formBuilder.control(subTopic.id));
        }
      });
    } else {
      while (subTopicArray.length > 0) {
        subTopicArray.removeAt(0);
      }
    }
  }

  areAllAreasSelected(
    topicIndex: number,
    areas: any[],
    areaType: string
  ): boolean {
    const selectedAreas = this.getSelectedAreas(topicIndex, areaType);
    const allAreaCodes = areas
      .filter((area) => area.type === areaType)
      .map((area) => area.code);

    return allAreaCodes.every((code) => selectedAreas.includes(code));
  }

  isSomeAreasSelected(
    topicIndex: number,
    areas: any[],
    areaType: string
  ): boolean {
    const selectedAreas = this.getSelectedAreas(topicIndex, areaType);
    const allAreaCodes = areas
      .filter((area) => area.type === areaType)
      .map((area) => area.code);

    return (
      selectedAreas.length > 0 && selectedAreas.length < allAreaCodes.length
    );
  }

  getSelectedAreas(topicIndex: number, areaType: string): string[] {
    const topicsFormArray = this.form.get('topics') as unknown as FormArray;
    const areaArray = topicsFormArray
      .at(topicIndex)
      .get('topicAreaIds') as FormArray;
    return areaArray.controls
      .filter((c) => c.value.type === areaType)
      .map((c) => c.value.code);
  }

  applyRequest(): void {
    this.prepareFormBeforeSubmit();

    const body = this.form.value as unknown as CreateServiceRequestType;

    this.storeHelperService
      .getAllDispatchAndSelect(new RequestsAction.CreateServiceRequest(body))
      .pipe(
        tap(() => {
          this.aranDrawerModalService.hide();
          this.store.dispatch(new RequestsAction.GetRequests());
        })
      )
      .subscribe();
  }

  prepareFormBeforeSubmit(): void {
    const topics = this.form.value.topics;

    if (!topics || !Array.isArray(topics) || !this.allServices?.topics) return;

    topics.forEach((topicFormItem: any) => {
      if (
        topicFormItem.subTopicSelectedOption !== 2 &&
        topicFormItem.subTopicSelectedOption !== null &&
        Array.isArray(topicFormItem.subTopicIds) &&
        topicFormItem.subTopicIds.length > 0
      ) {
        topicFormItem.subTopicIds = [];
      }

      if (topicFormItem.topicAreaSelectedOption !== 4) {
        const serviceTopic = this.allServices?.topics.find(
          (t) => t.id === topicFormItem.id
        );
        if (
          serviceTopic &&
          Array.isArray(serviceTopic.topicAreas) &&
          Array.isArray(topicFormItem.topicAreaIds)
        ) {
          const idsToRemove = serviceTopic.topicAreas
            .filter(
              (area) => area.type === 'Aerodrome' || area.type === 'Heliport'
            )
            .map((area) => area.id);

          topicFormItem.topicAreaIds = topicFormItem.topicAreaIds.filter(
            (id: number) => !idsToRemove.includes(id)
          );
        }
      }
    });
  }

  getCreateInfoList() {
    this.store.dispatch(new RequestsAction.GetCreateInfo()).subscribe();

    this.createInfo$.subscribe((data) => {
      this.selectedIndexes = data?.topics.map(() => 0) ?? [];
      this.initializeForm(data);

      data?.topics?.forEach((topic, index) => {
        const airportKey = this.getAirportKey(index);
        const firKey = this.getFirKey(index);
        const scenarioKey = this.getScenarioKey(index);

        this.filters[airportKey] = '';
        this.filters[firKey] = '';
        this.filters[scenarioKey] = '';

        this.pagingConfig[airportKey] = {
          id: airportKey,
          itemsPerPage: this.itemsPerPage,
          currentPage: this.currentPage,
          totalItems:
            topic.topicAreas?.filter((area) =>
              ['Aerodrome', 'Heliport'].includes(area.type)
            ).length || 0,
          footerTablePagination: true,
        };

        this.filterService.setPagingConfigValues(
          airportKey,
          this.pagingConfig[airportKey]
        );
        this.pagingConfig[airportKey] =
          this.filterService.getPagingConfigValues(airportKey);

        this.pagingConfig[firKey] = {
          id: firKey,
          itemsPerPage: this.itemsPerPage,
          currentPage: this.currentPage,
          totalItems:
            topic.topicAreas?.filter((area) => area.type === 'Airspace')
              .length || 0,
          footerTablePagination: true,
        };

        this.filterService.setPagingConfigValues(
          firKey,
          this.pagingConfig[firKey]
        );
        this.pagingConfig[firKey] =
          this.filterService.getPagingConfigValues(firKey);

        this.pagingConfig[scenarioKey] = {
          id: scenarioKey,
          itemsPerPage: this.itemsPerPage,
          currentPage: this.currentPage,
          totalItems: topic.subTopics?.length || 0,
          footerTablePagination: true,
        };

        this.filterService.setPagingConfigValues(
          scenarioKey,
          this.pagingConfig[scenarioKey]
        );
        this.pagingConfig[scenarioKey] =
          this.filterService.getPagingConfigValues(scenarioKey);
      });
    });
  }

  initializeForm(data: any): void {
    const topicsFormArray = this.form.get('topics') as unknown as FormArray;

    data?.topics.forEach((topic: any) => {
      const selectedSubTopicIds = (topic.subTopics || [])
        .filter((st: any) => st.isSelected)
        .map((st: any) => st.id);

      const selectedAreas = (topic.topicAreas || [])
        .filter((a: any) => a.isSelected)
        .map((st: any) => st.id);

      const selectedSubTopicOption =
        (topic.subTopicSelectionOptions || []).find(
          (opt: any) => opt.isSelected
        )?.value ?? null;
      const selectedTopicAreaOption =
        (topic.topicAreaSelectionOptions || []).find(
          (opt: any) => opt.isSelected
        )?.value ?? null;

      const topicGroup = this.formBuilder.group({
        id: [topic.id],
        subTopicSelectedOption: [selectedSubTopicOption],
        topicAreaSelectedOption: [selectedTopicAreaOption],
        subTopicIds: this.formBuilder.array(
          selectedSubTopicIds.map((id: number) => this.formBuilder.control(id))
        ),
        topicAreaIds: this.formBuilder.array(
          selectedAreas.map((id: number) => this.formBuilder.control(id))
        ),
      });

      topicsFormArray.push(topicGroup);
    });
  }

  onSubTopicSelectionChange(
    selectedValue: any,
    topicIndex: number,
    keyType: string
  ) {
    this.resetFilter(keyType);
    const topicsFormArray = this.form.get('topics') as unknown as FormArray;
    const topicGroup = topicsFormArray.at(topicIndex);

    if (topicGroup) {
      topicGroup.patchValue({ subTopicSelectedOption: selectedValue });
    }
  }

  onTopicAreaSelectionChange(
    selectedValue: any,
    topicIndex: number,
    topicAreas: any[]
  ): void {
    const topicsFormArray = this.form.get('topics') as unknown as FormArray;
    const topicGroup = topicsFormArray.at(topicIndex);

    if (topicGroup) {
      topicGroup.patchValue({ topicAreaSelectedOption: selectedValue });
    }
  }

  getSubTopicSelectedOption(topicIndex: number): number | null {
    const topicsFormArray = this.form.get('topics') as unknown as FormArray;
    const topicGroup = topicsFormArray.at(topicIndex);
    return topicGroup?.get('subTopicSelectedOption')?.value ?? null;
  }

  getTopicAreasSelectedOption(topicAreasIndex: number): number | null {
    const topicsFormArray = this.form.get('topics') as unknown as FormArray;
    const topicGroup = topicsFormArray.at(topicAreasIndex);
    return topicGroup?.get('topicAreaSelectedOption')?.value ?? null;
  }

  onHandlePaginate(event: number, tableKey: string) {
    this.filterService.updatePagingConfigValue(tableKey, 'currentPage', event);
  }

  onHandlePageSize(newSize: number, tableKey: string) {
    this.filterService.updatePagingConfigValue(tableKey, 'currentPage', 1);
    this.filterService.updatePagingConfigValue(
      tableKey,
      'itemsPerPage',
      newSize
    );
  }

  applyFilter(key: string): void {
    this.filterService.updatePagingConfigValue(key, 'currentPage', 1);

    this.filterService.updatePagingConfigValue(
      key,
      'totalItems',
      this.totalItems
    );
  }

  resetFilter(key: string) {
    this.filters[key] = '';
    this.filterService.updatePagingConfigValue(key, 'currentPage', 1);
  }

  onTabChanged(newIndex: number, itemIndex: number) {
    this.selectedIndexes[itemIndex] = newIndex;
  }
}
